import { defineStore } from "pinia";
import { JmApi } from "../jm-api/jm-api";
import {
  ADRESS_URL,
  ADRESS_ID_URL,
  ADRESS_WITH_ID_URL,
  PATHS_URL,
  PATHS_OFFICE_URL,
  PROJECTS_URL,
  UPLOAD_URL,
  USER_COMPANY_URL,
  PROJECT_USER_REL_URL,
} from "../config/urls";
import { Project } from "../jm-data-instances/project";
import { Company } from "../jm-data-instances/company";
import { Office } from "../jm-data-instances/offices";
import { Adress } from "../jm-data-instances/adress";
import { Shift } from "../jm-data-instances/shift";
import { Path } from "leaflet";
import { User, UserApi } from "../api/api";
import { UserProjectRelation } from "@/api/api";
import { Configuration, ConfigurationParameters } from "@/api";
import KeycloakService from "@/services/keycloak-service";

export const useOfficeStore = defineStore("office", {
  state: () => ({
    data: {
      projects: [] as object[],
      companies: [] as object[],
      offices: [] as object[],
      shifts: [] as object[],
      adresses: [] as object[],
      paths: [] as object[],
    },
    selectedProject: null as any,
    selectedCompany: null as any,
    selectedOffice: null as any,
    selectedShift: null as any,
  }),
  getters: {
    getProjects(state) {
      return state.data.projects;
    },
    getCompanies(state) {
      return state.data.companies;
    },
    getOffices(state) {
      return state.data.offices;
    },
    getShifts(state) {
      return state.data.shifts;
    },
    getAdresses(state) {
      return state.data.adresses;
    },
    getPaths(state) {
      return state.data.paths;
    },
    getSelectedProject: (state) => {
      // console.log("Store - getSelectedProject called:", state.selectedProject);
      return state.selectedProject;
    },
    getSelectedCompany: (state) => {
      // console.log("Store - getSelectedCompany called:", state.selectedCompany);
      return state.selectedCompany;
    },
    getSelectedOffice: (state) => {
      // console.log("Store - getSelectedOffice called:", state.selectedOffice);
      return state.selectedOffice;
    },
  },
  actions: {
    selectProject(project: object) {
      this.selectedProject = project;
    },
    selectCompany(company: object) {
      this.selectedCompany = company;
    },
    selectOffice(office: object) {
      this.selectedOffice = office;
    },
    selectShift(shift: object) {
      this.selectedShift = shift;
    },
    /**
     * Fetches paths based on office_id from jm-database.
     * NOTE: this function throws errors, that need to be caught.
     * @param office_id
     */
    async fetchPaths(
      office_id: number,
      originIds?: number[],
    ): Promise<object[]> {
      try {
        const response = await JmApi.get(
          `${PATHS_OFFICE_URL}?officeId=${office_id}`,
        );
        const data = response.data;
        this.data.paths = data;

        if (originIds && originIds.length > 0) {
          const filtered_paths = this.data.paths.filter((path) => {
            return originIds.includes(path.origin_id);
          });
          return filtered_paths;
        }

        return this.data.paths;
      } catch (error) {
        throw new Error(error.message);
      }
    },
    /**
     * Post request to store uploaded addresses from csv-file.
     * @param file
     */
    async postFile(data: {
      companyId: number;
      companyName: string;
      officeId?: number;
      shiftId?: number;
      fileData: any[];
    }) {
      try {
        const response = await JmApi.post(UPLOAD_URL, data);
        return response;
      } catch (error) {
        throw new Error("File upload failed");
      }
    },
    /**
     * Post request to store paths from temporary newPaths array.
     * @param paths list of paths created from csv file
     * @param office_id
     */
    async postPaths(paths: object[]) {
      try {
        const promises = paths.map((path) => JmApi.post(PATHS_URL, path));
        await Promise.all(promises);
      } catch (error) {
        throw new Error("Paths upload failed");
      }
    },
    /**
     * Fetches projects from jm-database.
     * NOTE: this function throws errors, that need to be caught.
     */
    async fetchProjects() {
      try {
        const data = await JmApi.get(PROJECTS_URL);
        // @ts-ignore
        this.data.projects = data.projects;
      } catch (error) {
        console.error("Error fetching projects:", error);
        throw new Error(error.message || "Failed to fetch projects");
      }
    },
    /**
     * Posts a project.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param project validated project to be posted (without id)
     */
    async postProject(project: object) {
      JmApi.post(PROJECTS_URL, project)
        .then((_) => {
          this.fetchProjects();
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Deletes existing project.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param projId identifier of existing project.
     * NOTE: it is assumed, the project exists.
     */
    async deleteProject(projId: number) {
      JmApi.delete(Project.deleteUrlBuilder(projId))
        .then((_) => {
          this.fetchProjects();
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Manipualtes existing project.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param project validated project (with id)
     */
    async putProject(project: object) {
      JmApi.put(Project.BASE_URL, project)
        .then((_) => {
          this.fetchProjects();
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Fetches companies from database.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param projId identifier of existing project.
     */

    async fetchCompanies(projId: number) {
      try {
        const data = await JmApi.get(Company.getUrlBuilder(projId));
        this.data.companies = data;
      } catch (error) {
        console.error("Error fetching companies:", error);
        throw new Error(error.message || "Failed to fetch companies");
      }
    },
    /**
     * Inserts company into database
     *
     * @param company valid company ({name: string})
     * @param projId identifier of existing project
     */
    async postCompany(company: object, projId: number) {
      JmApi.post(Company.postUrlBuilder(projId), company)
        .then((_) => {
          this.fetchCompanies(projId);
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Manipulates existing company.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param company valid company without id ({name: string, project_id: number})
     * @param id identifier of existing company (that should be manipulated)
     */
    async putCompany(company: object, id: number) {
      // @ts-ignore
      JmApi.put(Company.putUrlBuilder(id), company)
        .then((_) => {
          // @ts-ignore
          this.fetchCompanies(company.project_id);
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Deletes existing company in database.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param compId identifier of an existing company.
     * @param projId identifier of overarching project (so that the companies can be reloaded after successful deletion).
     */
    async deleteCompany(compId: number, projId: number) {
      JmApi.delete(Company.deleteUrlBuilder(compId))
        .then((_) => {
          // @ts-ignore
          this.fetchCompanies(projId);
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Fetches offices from database
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param projId identifier of overarching project.
     * @param compId identifier of overarching company.
     */
    async fetchOffices(projId: number, compId: number) {
      try {
        const data = await JmApi.get(Office.getUrlBuilder(projId, compId));
        // console.log("Offices fetched:", data);
        this.data.offices = data;
      } catch (error) {
        console.error("Error fetching offices:", error);
        throw new Error(error.message || "Failed to fetch offices");
      }
    },
    /**
     * Posts new office to database.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param office valid office (
     * {name: string,
     *  type: string,
     *  owner: string,
     *  adress: {
     *    street: string,
     *    house_number: string,
     *    postal_code: string,
     *    district: string,
     *    geo: {
     *      coodinates: [lon: number, lat: number],
     *      type: "Point"
     *     }
     *   },
     * })
     * @param compId identifier of existing overarching company.
     */
    async postOffice(office: object, projId: number, compId: number) {
      try {
        // @ts-ignore
        const adress = office.adress;
        // @ts-ignore
        delete office.adress;
        // Check if adress already exists in database
        const adressId = await this.getAdressByAdressData(
          adress.street,
          adress.house_number,
          adress.postal_code,
          adress.district,
        );

        if (typeof adressId === "number") {
          // @ts-ignore
          office["adress_id"] = adressId;
        } else {
          const data = await JmApi.post(Adress.postUrlBuilder(), adress);
          // @ts-ignore
          office["adress_id"] = data.id;
        }

        await JmApi.post(Office.postUrlBuilder(compId), office);
        await this.fetchOffices(projId, compId);
      } catch (error) {
        console.error("Error creating office:", error);
        throw new Error(error.message);
      }
    },
    /**
     * Manipulates an existing office.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param office valid office of type ({
     *  name: string,
     *  type: string,
     *  owner: string,
     *  adress: {
     *    street: string,
     *    house_number: string,
     *    postal_code: string,
     *    district: string,
     *    geo: {
     *      coodinates: [lon: number, lat: number],
     *      type: "Point"
     *     }
     *   },
     *  adress_id: number,
     * })
     * @param officeId: Identifier of office to be manipulated
     * @param compId Identifier of overarching company.
     * @param projId Identifier of overarching project
     */
    async putOffice(
      office: object,
      officeId: number,
      projId: number,
      compId: number,
    ) {
      // @ts-ignore
      const adress = office.adress;
      // @ts-ignore
      adress["id"] = office["adress_id"];
      // @ts-ignore
      delete office.adress;
      JmApi.put(Adress.postUrlBuilder(), adress)
        .then((_) => {
          JmApi.put(Office.putUrlBuilder(officeId), office)
            .then((_) => {
              this.fetchOffices(projId, compId);
            })
            .catch((data) => {
              throw new Error(data.message);
            });
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Deletes Office from the jm-database.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param officeId identifier of existing office.
     * @param projId Identifier of overarching project.
     * @param compId Identifier of overarching company.
     */
    async deleteOffice(projId: number, compId: number, officeId: number) {
      JmApi.delete(Office.deleteUrlBuilder(officeId))
        .then((_) => {
          this.fetchOffices(projId, compId);
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Fetches shifts from the jm-database.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param officeId Identifier of an existing overarching office.
     */
    async fetchShifts(officeId: number) {
      try {
        const data = await JmApi.get(Shift.getUrlBuilder(officeId));
        this.data.shifts = data;
        return this.data.shifts;
      } catch (error) {
        throw new Error(error.message);
      }
    },
    /**
     * Deletes existing shift from database.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param shiftId Identifier of a existing shift.
     * @param officeId Identifier of overarching office.
     */
    async deleteShift(shiftId: number, officeId: number) {
      JmApi.delete(Shift.deleteUrlBuilder(shiftId))
        .then((_) => {
          this.fetchShifts(officeId);
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Posts new shift to jm-database.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param shift Valid shift of type: {
     *   name: string,
     *   start: string(time-format),
     *   end: string(time-format)
     * }
     * @param officeId Identifier of existing office.
     */
    async postShift(shift: object, officeId: number) {
      JmApi.post(Shift.postUrlBuilder(officeId), shift)
        .then((_) => {
          this.fetchShifts(officeId);
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Manipulates existing shift in jm-database.
     *
     * @param shiftId Identifier of existing shift.
     * @param shift Valid shift of type: {
     *   name: string,
     *   start: string(time-format),
     *   end: string(time-format)
     * }
     * @param officeId
     */
    async putShift(shiftId: number, shift: object, officeId: number) {
      JmApi.put(Shift.putUrlBuilder(shiftId), shift)
        .then((_) => {
          this.fetchShifts(officeId);
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Fetches adresses from jm-database.
     * NOTE: this function throws errors, that need to be caught.
     */
    async fetchAdresses(
      project_id: number,
      company_id: number,
      office_id: number,
    ) {
      JmApi.get(
        ADRESS_URL +
          `?project_id=${project_id}&company_id=${company_id}&office_id=${office_id}`,
      )
        .then((data) => {
          this.data.adresses = data;
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Posts an adress.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param adress validated adress to be posted (without id)
     */
    async postAdress(adress: object) {
      JmApi.post(Adress.BASE_URL, adress)
        .then((_) => {
          this.fetchAdresses();
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Deletes existing adress.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param adressId identifier of existing adress.
     * NOTE: it is assumed, the adress exists.
     */
    async deleteAdress(adressId: number) {
      JmApi.delete(Adress.deleteUrlBuilder(adressId))
        .then((_) => {
          this.fetchProjects();
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Manipualtes existing adress.
     * NOTE: this function throws errors, that need to be caught.
     *
     * @param adress validated adress (with id)
     */
    async putAdress(adress: object) {
      JmApi.put(Adress.BASE_URL, adress)
        .then((_) => {
          this.fetchAdresses();
        })
        .catch((data) => {
          throw new Error(data.message);
        });
    },
    /**
     * Gets adress_id from database for existing adress.
     */
    async getAdressByAdressData(
      street: string,
      house_number: string,
      postal_code: string,
      district: string,
    ) {
      const url = `${ADRESS_ID_URL}?street=${street}&house_number=${house_number}&postal_code=${postal_code}&district=${district}`;
      try {
        const response = await JmApi.get(url);
        const data = response;

        return data;
      } catch (error) {
        throw new Error(error.message);
      }
    },

    /**
     * Fetch address data from database for an address with a given id.
     */
    async fetchAdressById(addressId: number) {
      try {
        const response = await JmApi.get(
          `${ADRESS_WITH_ID_URL}?adress_id=${addressId}`,
        );
        const data = response.data;
        return data;
      } catch (error) {
        throw new Error(error.message);
      }
    },
    async addUserToProject(userProjRel: UserProjectRelation) {
      // update token if it is outdated
      await KeycloakService.updateAccessToken();
      const accessTokenFunc = (name: string, scopes: string[]) =>
        KeycloakService.getAccessToken();
      const configParam: ConfigurationParameters = {
        accessToken: accessTokenFunc,
      };
      const config = new Configuration(configParam);
      new UserApi(config)
        .inviteUserToProject(userProjRel)
        .then((res) => {
          // TODO: add user to store as another participant in the project. User-id is needed to remove the user from the list or change his role
          const userId = res.userId;
        })
        .catch((err) => {
        });
    },
    // Delete multiple addresses in one API call
    async deleteAddressesBatch(addressIds: number[]): Promise<void> {
      try {
        await JmApi.delete(`${ADRESS_URL}/batch`, { address_ids: addressIds });
      } catch (error) {
        console.error("Error batch deleting addresses:", error);
        throw error;
      }
    },
    // Check if addresses are used by other offices
    async checkAddressesUsedByOtherOffices(
      addressIds: number[],
      currentOfficeId: number,
    ): Promise<number[]> {
      try {
        // Using POST to send the list of addressIds to check
        const response = await JmApi.post(`${ADRESS_URL}/check-usage`, {
          addressIds,
          excludeOfficeId: currentOfficeId,
        });

        // The response should contain addressIds that are used by other offices
        return response.used_address_ids || [];
      } catch (error) {
        console.error("Error checking address usage:", error);
        return [];
      }
    },
    // Delete paths for an office
    async deletePathsForOffice(paths: number[]): Promise<void> {
      try {
        await JmApi.delete(`${PATHS_OFFICE_URL}`, { path_ids: paths });
      } catch (error) {
        console.error("Error deleting paths for office:", error);
        throw error;
      }
    },
    // Delete user_company_relation
    async deleteUserCompanyRelation(companyId: number): Promise<void> {
      // console.log("Deleting user_company_relation for companyId: ", companyId);
      try {
        await JmApi.delete(`${USER_COMPANY_URL}?CompanyId=${companyId}`);
      } catch (error) {
        console.error("Error deleting user_company_relation:", error);
        throw error;
      }
    },
    // Delete project_user_relation
    async deleteProjectUserRelation(projectId: number): Promise<void> {
      // console.log("Deleting project_user_relation for projectId: ", projectId);
      try {
        await JmApi.delete(`${PROJECT_USER_REL_URL}?projId=${projectId}`);
      } catch (error) {
        console.error("Error deleting project_user_relation:", error);
        throw error;
      }
    },
  },
});
